/**
 * Projekt-Analyse „Was ist passiert?“: vergleicht einen Monat mit dem Vormonat
 * (Umsatz, Kosten nach Kostengruppe, Stunden) und leitet Margenbrücke,
 * Leistungskennzahlen und Auffälligkeiten ab. Reine Rechenlogik, von Server
 * (Report) und Oberfläche (Deep Dive) gemeinsam genutzt.
 *
 * Beträge in Cent, Kosten positiv. Monate als Index 0–11.
 */
export const COST_GROUPS = ['personnel', 'subcontracting', 'other'];
const EMPTY = { netSalesCents: 0, hours: 0, costs: { personnel: 0, subcontracting: 0, other: 0 } };
export function derive(m) {
    const f = m ?? EMPTY;
    const costCents = f.costs.personnel + f.costs.subcontracting + f.costs.other;
    const marginCents = f.netSalesCents - costCents;
    return {
        netSalesCents: f.netSalesCents,
        costCents,
        marginCents,
        marginPct: f.netSalesCents > 0 ? marginCents / f.netSalesCents : null,
        hours: f.hours,
        salesPerHourCents: f.hours > 0 ? f.netSalesCents / f.hours : null,
        costPerHourCents: f.hours > 0 ? costCents / f.hours : null,
        costs: { ...f.costs },
    };
}
/** relative Änderung a gegenüber b; 0, wenn b = 0 */
export function relChange(a, b) {
    return b ? (a - b) / b : 0;
}
const SEV_ORDER = ['critical', 'watch', 'info', 'ok'];
export function worstSeverity(list) {
    for (const s of SEV_ORDER)
        if (list.includes(s))
            return s;
    return 'ok';
}
/** Schwellen (Cent) – zentral, damit Tests und Doku sie nennen können */
export const ANALYSIS_RULES = {
    /** ab dieser Umsatzänderung zählt ein Rückgang/Anstieg (10 € → 1.000 €) */
    salesDeltaCents: 100_000,
    /** ab dieser Kostenänderung zählt ein Anstieg */
    costDeltaCents: 100_000,
    /** Umsatz je Stunde fällt um mindestens 10 % */
    rateDrop: 0.1,
    /** ab dieser Margenwirkung ist ein Rückgang je Stunde kritisch (5.000 €) */
    criticalMarginCents: 500_000,
    /** Kosten je Stunde steigen um mindestens 10 % bei Personalkosten +3.000 € */
    costlierHours: 0.1,
    costlierPersonnelCents: 300_000,
    /** sonstige Kosten steigen um mindestens 500 € */
    otherUpCents: 50_000,
    /** kaum Stunden: unter 10 h, aber Kosten über 500 € */
    idleHours: 10,
    idleCostCents: 50_000,
    /** Änderung der Marge unter 1.500 € gilt als stabil (Text) */
    stableMarginCents: 150_000,
};
/**
 * Analysiert Monat `month` gegen den Vormonat.
 * `months`: Index 0–11, fehlende Monate = null. `target`: Zielmarge 0–1.
 */
export function analyzeMonth(months, month, target) {
    const R = ANALYSIS_RULES;
    const d = months.map(derive);
    const cur = d[month] ?? derive(null);
    const prevMonth = Math.max(0, month - 1);
    const prev = month > 0 ? d[prevMonth] ?? derive(null) : derive(null);
    const isStart = month === 0 || prev.netSalesCents <= 0;
    const detail = (months[month]?.costDetail ?? true) && (month === 0 || (months[prevMonth]?.costDetail ?? true));
    const dSales = cur.netSalesCents - prev.netSalesCents;
    const dCost = {
        personnel: cur.costs.personnel - prev.costs.personnel,
        subcontracting: cur.costs.subcontracting - prev.costs.subcontracting,
        other: cur.costs.other - prev.costs.other,
    };
    const dCostTotal = cur.costCents - prev.costCents;
    const dMargin = cur.marginCents - prev.marginCents;
    const bridge = detail ? [
        { key: 'marginPrev', valueCents: prev.marginCents },
        { key: 'netSales', valueCents: dSales },
        { key: 'personnel', valueCents: -dCost.personnel },
        ...(cur.costs.subcontracting || prev.costs.subcontracting ? [{ key: 'subcontracting', valueCents: -dCost.subcontracting }] : []),
        { key: 'other', valueCents: -dCost.other },
        { key: 'marginCur', valueCents: cur.marginCents },
    ] : [
        { key: 'marginPrev', valueCents: prev.marginCents },
        { key: 'netSales', valueCents: dSales },
        { key: 'costs', valueCents: -dCostTotal },
        { key: 'marginCur', valueCents: cur.marginCents },
    ];
    // Haupttreiber: größte Wirkung in Richtung der Margenänderung
    const drivers = detail ? [
        { k: 'netSales', v: dSales },
        { k: 'personnel', v: -dCost.personnel },
        { k: 'subcontracting', v: -dCost.subcontracting },
        { k: 'other', v: -dCost.other },
    ] : [{ k: 'netSales', v: dSales }, { k: 'costs', v: -dCostTotal }];
    const same = drivers.filter((x) => x.v !== 0 && (x.v < 0) === (dMargin < 0)).sort((a, b) => Math.abs(b.v) - Math.abs(a.v));
    const mainDriver = isStart || Math.abs(dMargin) < R.stableMarginCents && same.length === 0 ? null : (same[0]?.k ?? null);
    const findings = [];
    const push = (code, severity, params = {}) => findings.push({ code, severity, params });
    const hoursChange = relChange(cur.hours, prev.hours);
    const salesChange = relChange(cur.netSalesCents, prev.netSalesCents);
    const costChange = relChange(cur.costCents, prev.costCents);
    if (!isStart) {
        if (dSales < -R.salesDeltaCents && dCostTotal > R.costDeltaCents) {
            push('scissors', 'critical', { salesDelta: dSales, costDelta: dCostTotal });
        }
        const rateDrop = prev.hours > 20 && cur.hours > 20 && prev.salesPerHourCents !== null && cur.salesPerHourCents !== null
            && relChange(cur.salesPerHourCents, prev.salesPerHourCents) < -R.rateDrop;
        if (rateDrop) {
            push('rateDrop', Math.abs(dMargin) > R.criticalMarginCents ? 'critical' : 'watch', {
                hoursChange, salesChange, ratePrev: prev.salesPerHourCents, rateCur: cur.salesPerHourCents,
                rateChange: relChange(cur.salesPerHourCents, prev.salesPerHourCents), moreWork: hoursChange > -0.05 ? 1 : 0,
            });
        }
        if (dSales < -R.salesDeltaCents && hoursChange <= -0.05 && !rateDrop) {
            push('lessWork', 'info', { hoursPrev: prev.hours, hoursCur: cur.hours, hoursChange, salesChange, costChange, marginPtsChange: (cur.marginPct ?? 0) - (prev.marginPct ?? 0) });
        }
        if (dSales > R.salesDeltaCents && hoursChange < 0.05 && prev.salesPerHourCents && cur.salesPerHourCents && relChange(cur.salesPerHourCents, prev.salesPerHourCents) > R.rateDrop) {
            push('rateUp', 'ok', { ratePrev: prev.salesPerHourCents, rateCur: cur.salesPerHourCents });
        }
        if ((detail ? dCost.personnel : dCostTotal) > R.costlierPersonnelCents && prev.costPerHourCents && cur.costPerHourCents && relChange(cur.costPerHourCents, prev.costPerHourCents) > R.costlierHours) {
            push('costlierHours', 'watch', { cphPrev: prev.costPerHourCents, cphCur: cur.costPerHourCents, change: relChange(cur.costPerHourCents, prev.costPerHourCents) });
        }
        if (detail && cur.costs.subcontracting > 0 && dSales < 0 && Math.abs(dCost.subcontracting) < 10_000) {
            push('fixedSubcontract', 'watch', {
                amount: cur.costs.subcontracting,
                shareCur: cur.netSalesCents ? cur.costs.subcontracting / cur.netSalesCents : 0,
                sharePrev: prev.netSalesCents ? prev.costs.subcontracting / prev.netSalesCents : 0,
            });
        }
        if (detail && dCost.other > R.otherUpCents)
            push('otherUp', 'watch', { delta: dCost.other, amount: cur.costs.other });
    }
    // Umsatz sinkt seit n Monaten
    let falling = 0;
    for (let i = month; i > 0; i--) {
        const a = d[i]?.netSalesCents ?? 0, b = d[i - 1]?.netSalesCents ?? 0;
        if (b > 0 && a < b - 50_000)
            falling++;
        else
            break;
    }
    if (falling >= 2) {
        const from = month - falling;
        push('salesFalling', falling >= 3 ? 'critical' : 'watch', { months: falling, fromMonth: from, fromCents: d[from].netSalesCents, toCents: cur.netSalesCents, change: relChange(cur.netSalesCents, d[from].netSalesCents) });
    }
    // Marge n Monate in Folge unter Ziel
    let below = 0;
    for (let i = month; i >= 0; i--) {
        const p = d[i]?.marginPct;
        if (p !== null && p !== undefined && p < target)
            below++;
        else
            break;
    }
    if (below >= 2)
        push('belowTarget', 'watch', { months: below, target, marginPct: cur.marginPct ?? 0 });
    if (cur.marginPct !== null && cur.marginPct < 0)
        push('negativeMargin', 'critical', { cost: cur.costCents, sales: cur.netSalesCents });
    if (cur.hours < R.idleHours && cur.costCents > R.idleCostCents)
        push('idleCosts', 'watch', { hours: cur.hours, cost: cur.costCents });
    if (isStart && cur.netSalesCents > 0)
        push('start', 'info', { sales: cur.netSalesCents, cost: cur.costCents, marginPct: cur.marginPct ?? 0 });
    if (!findings.length)
        push('stable', 'ok');
    const rank = (s) => SEV_ORDER.indexOf(s);
    findings.sort((a, b) => rank(a.severity) - rank(b.severity));
    const severity = worstSeverity(findings.map((f) => f.severity).filter((s) => s !== 'info'));
    return { month, prevMonth, isStart, cur, prev, bridge, mainDriver, costDetail: detail, findings, severity: findings.every((f) => f.severity === 'info') ? 'ok' : severity };
}
/** Letzter Monat mit Umsatz oder Kosten (Index) oder -1 */
export function lastActiveMonth(months) {
    for (let i = months.length - 1; i >= 0; i--) {
        const m = months[i];
        if (m && m.netSalesCents > 0)
            return i;
    }
    return -1;
}
const MONTHS = {
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};
export function monthName(i, lang) { return MONTHS[lang][i] ?? ''; }
const loc = (lang) => (lang === 'de' ? 'de-DE' : 'en-GB');
const MINUS = '−';
export function fmtEur(cents, lang) {
    const v = Math.round(Math.abs(cents) / 100).toLocaleString(loc(lang));
    return (cents < 0 ? MINUS : '') + v + ' €';
}
export function fmtK(cents, lang, signed = false) {
    const v = (Math.abs(cents) / 100_000).toLocaleString(loc(lang), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const sign = cents > 0 ? (signed ? '+' : '') : cents < 0 ? MINUS : signed ? '±' : '';
    return sign + v + (lang === 'de' ? ' T€' : 'k €');
}
export function fmtPct(v, signed = false) {
    const r = Math.round(v * 100);
    return (r > 0 && signed ? '+' : r < 0 ? MINUS : r === 0 && signed ? '±' : '') + Math.abs(r) + ' %';
}
function fmtPts(v, lang) {
    const r = Math.round(v * 100);
    return (r > 0 ? '+' : r < 0 ? MINUS : '±') + Math.abs(r) + (lang === 'de' ? ' Punkte' : ' points');
}
function fmtH(h, lang) { return Math.round(h).toLocaleString(loc(lang)) + ' h'; }
const DRIVER = {
    de: { netSales: 'Umsatz', personnel: 'Personalkosten', subcontracting: 'Subunternehmer', other: 'Sonstige Kosten', costs: 'Projektkosten' },
    en: { netSales: 'net sales', personnel: 'personnel costs', subcontracting: 'subcontractors', other: 'other costs', costs: 'project costs' },
};
export const COST_GROUP_LABEL = {
    de: { personnel: 'Personal', subcontracting: 'Subunternehmer', other: 'Sonstige' },
    en: { personnel: 'Personnel', subcontracting: 'Subcontractors', other: 'Other' },
};
export const SEVERITY_LABEL = {
    de: { critical: 'Handlungsbedarf', watch: 'beobachten', info: 'Hinweis', ok: 'unauffällig' },
    en: { critical: 'Action needed', watch: 'Watch', info: 'Note', ok: 'Unremarkable' },
};
/** Zusammenfassung in 1–2 Sätzen */
export function summaryText(a, lang) {
    const { cur, prev } = a;
    if (a.isStart) {
        return lang === 'de'
            ? `Erster Monat mit Umsatz: ${fmtEur(cur.netSalesCents, lang)} Umsatz, ${fmtEur(cur.costCents, lang)} Kosten, Marge ${cur.marginPct === null ? '–' : fmtPct(cur.marginPct)}.`
            : `First month with sales: ${fmtEur(cur.netSalesCents, lang)} net sales, ${fmtEur(cur.costCents, lang)} costs, margin ${cur.marginPct === null ? '–' : fmtPct(cur.marginPct)}.`;
    }
    const dM = cur.marginCents - prev.marginCents;
    const drv = a.mainDriver;
    const drvVal = drv === 'netSales' ? cur.netSalesCents - prev.netSalesCents : drv === 'costs' ? cur.costCents - prev.costCents : drv ? cur.costs[drv] - prev.costs[drv] : 0;
    const drvTxt = drv ? `${DRIVER[lang][drv]} ${fmtK(drvVal, lang, true)}` : '';
    const mp = cur.marginPct ?? 0, pp = prev.marginPct ?? 0;
    const tail = lang === 'de'
        ? `Umsatz ${fmtPct(relChange(cur.netSalesCents, prev.netSalesCents), true)}, Kosten ${fmtPct(relChange(cur.costCents, prev.costCents), true)}, Stunden ${fmtPct(relChange(cur.hours, prev.hours), true)}.`
        : `Net sales ${fmtPct(relChange(cur.netSalesCents, prev.netSalesCents), true)}, costs ${fmtPct(relChange(cur.costCents, prev.costCents), true)}, hours ${fmtPct(relChange(cur.hours, prev.hours), true)}.`;
    if (Math.abs(mp - pp) < 0.02) {
        const abs = Math.abs(dM) >= ANALYSIS_RULES.stableMarginCents && drv;
        return lang === 'de'
            ? `Die Marge bleibt mit ${fmtPct(mp)} nahezu stabil (Vormonat ${fmtPct(pp)})${abs ? `, absolut ${fmtK(dM, lang, true)} – Haupttreiber: ${drvTxt}` : ''}. ${tail}`
            : `The margin stays almost stable at ${fmtPct(mp)} (previous month ${fmtPct(pp)})${abs ? `, ${fmtK(dM, lang, true)} in absolute terms – main driver: ${drvTxt}` : ''}. ${tail}`;
    }
    return lang === 'de'
        ? `Die Marge ${mp < pp ? 'sinkt' : 'steigt'} von ${fmtPct(pp)} auf ${fmtPct(mp)} (${fmtPts(mp - pp, lang)}, ${fmtK(dM, lang, true)}).${drv ? ` Haupttreiber: ${drvTxt}.` : ''} ${tail}`
        : `The margin ${mp < pp ? 'falls' : 'rises'} from ${fmtPct(pp)} to ${fmtPct(mp)} (${fmtPts(mp - pp, lang)}, ${fmtK(dM, lang, true)}).${drv ? ` Main driver: ${drvTxt}.` : ''} ${tail}`;
}
/** Titel, Text und Hinweis einer Auffälligkeit */
export function findingText(f, month, lang) {
    const p = f.params;
    const de = lang === 'de';
    const M = monthName(month, lang);
    switch (f.code) {
        case 'scissors':
            return de
                ? { title: 'Umsatz sinkt, Kosten steigen', text: `Umsatz ${fmtK(p.salesDelta, lang, true)} bei ${fmtK(p.costDelta, lang, true)} Kosten. Beides drückt die Marge gleichzeitig.`, hint: 'Prüfen: Wurde alles abgerechnet? Ist das Team größer geworden als beauftragt?' }
                : { title: 'Sales down, costs up', text: `Net sales ${fmtK(p.salesDelta, lang, true)} with ${fmtK(p.costDelta, lang, true)} costs. Both squeeze the margin at once.`, hint: 'Check: was everything invoiced? Has the team grown beyond the order?' };
        case 'rateDrop':
            return de
                ? { title: p.moreWork ? 'Gleich viel oder mehr Arbeit, weniger Umsatz je Stunde' : 'Umsatz sinkt stärker als die Stunden', text: `Stunden ${fmtPct(p.hoursChange, true)}, Umsatz ${fmtPct(p.salesChange, true)}. Umsatz je Stunde fällt von ${fmtEur(p.ratePrev, lang)} auf ${fmtEur(p.rateCur, lang)} (${fmtPct(p.rateChange, true)}).`, hint: 'Mögliche Ursachen: Budget- oder Festpreisdeckel, nicht abrechenbare Stunden, Abrechnung verschoben.' }
                : { title: p.moreWork ? 'Same or more work, less sales per hour' : 'Sales fall faster than hours', text: `Hours ${fmtPct(p.hoursChange, true)}, net sales ${fmtPct(p.salesChange, true)}. Sales per hour drop from ${fmtEur(p.ratePrev, lang)} to ${fmtEur(p.rateCur, lang)} (${fmtPct(p.rateChange, true)}).`, hint: 'Possible causes: budget or fixed-price cap, non-billable hours, invoicing postponed.' };
        case 'lessWork':
            return de
                ? { title: 'Weniger geleistet', text: `Stunden ${fmtPct(p.hoursChange, true)} (${fmtH(p.hoursPrev, lang)} → ${fmtH(p.hoursCur, lang)}), Umsatz ${fmtPct(p.salesChange, true)}, Kosten ${fmtPct(p.costChange, true)}.`, hint: `Typisch bei Urlaub, Krankheit oder Teamverkleinerung. Die Marge ${Math.abs(p.marginPtsChange) < 0.03 ? 'bleibt dabei stabil.' : `verändert sich dabei um ${fmtPts(p.marginPtsChange, lang)}.`}` }
                : { title: 'Less work delivered', text: `Hours ${fmtPct(p.hoursChange, true)} (${fmtH(p.hoursPrev, lang)} → ${fmtH(p.hoursCur, lang)}), net sales ${fmtPct(p.salesChange, true)}, costs ${fmtPct(p.costChange, true)}.`, hint: `Typical for holidays, sickness or a smaller team. The margin ${Math.abs(p.marginPtsChange) < 0.03 ? 'stays stable.' : `changes by ${fmtPts(p.marginPtsChange, lang)}.`}` };
        case 'rateUp':
            return de
                ? { title: 'Mehr Umsatz je Stunde', text: `Umsatz je Stunde steigt von ${fmtEur(p.ratePrev, lang)} auf ${fmtEur(p.rateCur, lang)}.`, hint: 'Prüfen, ob eine Nachberechnung aus Vormonaten enthalten ist.' }
                : { title: 'More sales per hour', text: `Sales per hour rise from ${fmtEur(p.ratePrev, lang)} to ${fmtEur(p.rateCur, lang)}.`, hint: 'Check whether it includes catch-up invoicing from earlier months.' };
        case 'costlierHours':
            return de
                ? { title: 'Teurere Stunden', text: `Kosten je Stunde steigen von ${fmtEur(p.cphPrev, lang)} auf ${fmtEur(p.cphCur, lang)} (${fmtPct(p.change, true)}).`, hint: 'Hinweis auf erfahrenere Leute im Team, Teamwechsel oder Kosten ohne Zeiterfassung.' }
                : { title: 'More expensive hours', text: `Cost per hour rises from ${fmtEur(p.cphPrev, lang)} to ${fmtEur(p.cphCur, lang)} (${fmtPct(p.change, true)}).`, hint: 'Points to more senior people, team changes or costs without time tracking.' };
        case 'fixedSubcontract':
            return de
                ? { title: 'Subunternehmer bleiben fix, Umsatz sinkt', text: `Subunternehmer unverändert ${fmtEur(p.amount, lang)} im Monat, das sind jetzt ${fmtPct(p.shareCur)} des Umsatzes (Vormonat ${fmtPct(p.sharePrev)}).`, hint: 'Fixe Fremdkosten schlagen bei sinkendem Umsatz voll auf die Marge durch.' }
                : { title: 'Subcontractors fixed, sales falling', text: `Subcontractors unchanged at ${fmtEur(p.amount, lang)} per month, now ${fmtPct(p.shareCur)} of net sales (previous month ${fmtPct(p.sharePrev)}).`, hint: 'Fixed external costs hit the margin fully when sales fall.' };
        case 'otherUp':
            return de
                ? { title: 'Sonstige Kosten steigen', text: `Sonstige Kosten ${fmtK(p.delta, lang, true)} auf ${fmtEur(p.amount, lang)} (v. a. Software-Abos und Reisen).`, hint: 'Lizenzen und Abos prüfen: Werden sie im Projekt noch gebraucht, sind sie abrechenbar?' }
                : { title: 'Other costs rising', text: `Other costs ${fmtK(p.delta, lang, true)} to ${fmtEur(p.amount, lang)} (mainly software subscriptions and travel).`, hint: 'Check licences and subscriptions: still needed, billable?' };
        case 'salesFalling':
            return de
                ? { title: `Umsatz sinkt seit ${p.months} Monaten`, text: `Von ${fmtEur(p.fromCents, lang)} (${monthName(p.fromMonth, lang)}) auf ${fmtEur(p.toCents, lang)} (${fmtPct(p.change, true)}).`, hint: 'Mit Bestellungen und Forecast abgleichen: geplant oder Abweichung?' }
                : { title: `Sales falling for ${p.months} months`, text: `From ${fmtEur(p.fromCents, lang)} (${monthName(p.fromMonth, lang)}) to ${fmtEur(p.toCents, lang)} (${fmtPct(p.change, true)}).`, hint: 'Compare with orders and forecast: planned or a deviation?' };
        case 'belowTarget':
            return de
                ? { title: `Marge ${p.months} Monate in Folge unter Ziel`, text: `Zielmarge ${fmtPct(p.target)}, zuletzt ${fmtPct(p.marginPct)}.`, hint: 'Dauerthema: Preise, Teamzusammensetzung oder Umfang prüfen.' }
                : { title: `Margin below target for ${p.months} months`, text: `Target margin ${fmtPct(p.target)}, latest ${fmtPct(p.marginPct)}.`, hint: 'Persistent topic: review rates, team mix or scope.' };
        case 'negativeMargin':
            return de
                ? { title: `Negative Marge im ${M}`, text: `Kosten ${fmtEur(p.cost, lang)} übersteigen den Umsatz von ${fmtEur(p.sales, lang)}.`, hint: 'Sofort klären, ob Umsatz fehlt oder Kosten falsch zugeordnet sind.' }
                : { title: `Negative margin in ${M}`, text: `Costs of ${fmtEur(p.cost, lang)} exceed net sales of ${fmtEur(p.sales, lang)}.`, hint: 'Clarify right away whether sales are missing or costs are misallocated.' };
        case 'idleCosts':
            return de
                ? { title: 'Kaum Projektarbeit, laufende Kosten', text: `${Math.round(p.hours)} Stunden im ${M}, aber ${fmtEur(p.cost, lang)} Kosten (v. a. Software-Abos).`, hint: 'Prüfen, ob die Abos noch gebraucht oder dem Kunden weiterberechnet werden.' }
                : { title: 'Hardly any project work, ongoing costs', text: `${Math.round(p.hours)} hours in ${M}, but ${fmtEur(p.cost, lang)} costs (mainly software subscriptions).`, hint: 'Check whether the subscriptions are still needed or recharged to the customer.' };
        case 'start':
            return de
                ? { title: 'Projektstart', text: 'Erster Monat mit Umsatz und Kosten. Vergleich zum Vormonat ab dem nächsten Monat.', hint: '' }
                : { title: 'Project start', text: 'First month with sales and costs. Month-on-month comparison from next month.', hint: '' };
        case 'stable':
        default:
            return de
                ? { title: 'Keine Auffälligkeiten', text: 'Umsatz, Kosten und Stunden im üblichen Rahmen.', hint: '' }
                : { title: 'Nothing unusual', text: 'Sales, costs and hours within the usual range.', hint: '' };
    }
}
/** Kurzzeile für Listen („Marge 41 % → −3 %; Umsatz −38,0 T€, Kosten +13,0 T€“) */
export function shortLine(a, lang) {
    if (a.isStart)
        return lang === 'de' ? `Projektstart, Marge ${a.cur.marginPct === null ? '–' : fmtPct(a.cur.marginPct)}` : `Project start, margin ${a.cur.marginPct === null ? '–' : fmtPct(a.cur.marginPct)}`;
    const pp = a.prev.marginPct, mp = a.cur.marginPct;
    return (lang === 'de' ? 'Marge ' : 'Margin ') + (pp === null ? '–' : fmtPct(pp)) + ' → ' + (mp === null ? '–' : fmtPct(mp))
        + (lang === 'de' ? '; Umsatz ' : '; sales ') + fmtK(a.cur.netSalesCents - a.prev.netSalesCents, lang, true)
        + (lang === 'de' ? ', Kosten ' : ', costs ') + fmtK(a.cur.costCents - a.prev.costCents, lang, true);
}
//# sourceMappingURL=analysis.js.map